/* UFORIQ location selector — v3
 * - Persists choice to localStorage (`uforiq.location`)
 * - Mirrors choice into the Krown "Local" theme store-selector via
 *   localStorage["selected-store"] + a `storechanged` event so the theme's
 *   built-in pickup availability widget + header label update everywhere.
 * - Reflects choice on body[data-uforiq-location] for CSS hooks
 * - Updates header chip
 * - Fires `uforiq:location:changed` on window for downstream extensions
 * - Auto-opens the modal on first visit if no choice stored
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'uforiq.location';
  var THEME_STORAGE_KEY = 'selected-store';
  var VALID = ['austin', 'lubbock', 'nationwide'];
  var LABELS = {
    austin: 'Austin Metro',
    lubbock: 'Lubbock Metro',
    nationwide: 'Nationwide'
  };
  // Default Krown store-selector block handles. Schema/snippet override per button via data-uforiq-store-handle.
  var DEFAULT_HANDLES = {
    austin: 'central-texas',
    lubbock: 'west-texas',
    nationwide: 'nationwide-shipping'
  };

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && VALID.indexOf(parsed.location) !== -1) return parsed;
    } catch (e) { /* ignore */ }
    return null;
  }

  function write(location) {
    var payload = { location: location, choseAt: new Date().toISOString() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch (e) { /* ignore */ }
    return payload;
  }

  function applyToDom(location) {
    document.body.setAttribute('data-uforiq-location', location || '');
    document.querySelectorAll('[data-uforiq-choice]').forEach(function (btn) {
      btn.setAttribute('aria-checked', btn.getAttribute('data-uforiq-choice') === location ? 'true' : 'false');
    });
    var chip = document.querySelector('[data-uforiq-chip]');
    var chipLabel = document.querySelector('[data-uforiq-chip-label]');
    if (chip) chip.hidden = !location;
    if (chipLabel) chipLabel.textContent = location ? LABELS[location] : 'Choose area';
  }

  function dispatch(location, source) {
    window.dispatchEvent(new CustomEvent('uforiq:location:changed', {
      detail: { location: location, source: source || 'unknown' }
    }));
  }

  function resolveStoreHandle(location) {
    var btn = document.querySelector('[data-uforiq-choice="' + location + '"][data-uforiq-store-handle]');
    if (btn) {
      var handle = btn.getAttribute('data-uforiq-store-handle');
      if (handle) return handle;
    }
    return DEFAULT_HANDLES[location] || null;
  }

  /* Mirror the choice into the Krown "Local" theme's store-selector by walking
   * the theme's own commit flow:
   *   a) click the matching .store-selector-item__input-container so the
   *      <store-selector> element marks that block as "active"
   *   b) click [data-js-select-store] so Krown's own click handler:
   *        - writes localStorage["selected-store"]
   *        - dispatches `storechanged` on the <store-selector> (consumed by
   *          pickup-availability widgets, etc.)
   *        - calls changeSelectedStoreHandleContent() which updates the
   *          header store-selector block label ([data-type="store-selector"])
   *
   * If the <store-selector> custom element isn't yet upgraded (timing race
   * with Krown's deferred script), we still set localStorage so a subsequent
   * page load picks up the right store, and retry the DOM flow on the next
   * frame a few times.
   */
  function syncToThemeStoreSelector(location, attempt) {
    attempt = attempt || 0;
    var handle = resolveStoreHandle(location);
    if (!handle) return;
    try { localStorage.setItem(THEME_STORAGE_KEY, handle); } catch (e) { /* ignore */ }

    var selectorEl = document.querySelector('store-selector[data-main-selector]');
    var ssDefined = typeof customElements !== 'undefined' && customElements.get && !!customElements.get('store-selector');
    if (!selectorEl || !ssDefined) {
      if (attempt < 30) {
        setTimeout(function () { syncToThemeStoreSelector(location, attempt + 1); }, 100);
      }
      return;
    }

    var item = selectorEl.querySelector('.store-selector-item[data-handle="' + handle + '"]');
    if (!item) return;

    if (!item.classList.contains('store-selector-item--store--active')) {
      var input = item.querySelector('.store-selector-item__input-container');
      if (input) input.click();
    }
    var commit = document.querySelector('[data-js-select-store]');
    if (commit) commit.click();
  }

  function setLocation(location, source) {
    if (VALID.indexOf(location) === -1) return;
    write(location);
    applyToDom(location);
    syncToThemeStoreSelector(location);
    dispatch(location, source);
    closeModal();
  }

  function getModal() { return document.querySelector('[data-uforiq-modal]'); }

  function openModal() {
    var m = getModal();
    if (!m) return;
    if (typeof m.showModal === 'function') m.showModal();
    else m.setAttribute('open', '');
  }

  function closeModal() {
    var m = getModal();
    if (!m) return;
    if (typeof m.close === 'function') m.close();
    else m.removeAttribute('open');
  }

  function bind() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-uforiq-choice]');
      if (btn) {
        e.preventDefault();
        setLocation(btn.getAttribute('data-uforiq-choice'), 'button');
        return;
      }
      if (e.target.closest('[data-uforiq-chip]')) {
        e.preventDefault();
        openModal();
        return;
      }
      if (e.target.closest('[data-uforiq-modal-close]')) {
        e.preventDefault();
        closeModal();
        return;
      }
    });
  }

  function locationFromThemeHandle(handle) {
    if (!handle) return null;
    for (var i = 0; i < VALID.length; i++) {
      if (resolveStoreHandle(VALID[i]) === handle) return VALID[i];
    }
    return null;
  }

  function init() {
    bind();
    var stored = read();
    if (stored) {
      applyToDom(stored.location);
      syncToThemeStoreSelector(stored.location);
      dispatch(stored.location, 'restore');
      return;
    }
    // No uforiq choice — try to adopt the theme's existing store-selector value.
    var themeHandle = null;
    try { themeHandle = localStorage.getItem(THEME_STORAGE_KEY); } catch (e) { /* ignore */ }
    var adopted = locationFromThemeHandle(themeHandle);
    if (adopted) {
      write(adopted);
      applyToDom(adopted);
      dispatch(adopted, 'theme-adopt');
      return;
    }
    applyToDom(null);
    // First-visit prompt — wait a beat so the page paints first.
    setTimeout(openModal, 600);
  }

  // Public API for other scripts (per-location availability extension, analytics, etc.)
  window.UforiqLocation = {
    get: function () { var s = read(); return s ? s.location : null; },
    set: function (location) { setLocation(location, 'api'); },
    open: openModal,
    close: closeModal,
    LABELS: LABELS
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
